import {
  Table,
  ColumnDefinition,
  RowDefinition,
  palettes,
  TinyTitle,
  Card,
  SmallBody
} from '@indigo-ag/component-module';
import { Detail } from '@indigo-ag/schema';
import React from 'react';
import { currency, date } from 'shared';
import i18next from 'i18next';
import styled from 'styled-components';

interface Props {
  details: Detail[];
}

export type ColumnId = keyof Omit<Detail, 'contractId' | 'legalNatureId' | 'principal'>;

const TableCell = styled.div`
  font-size: 0.75rem;
  font-weight: 300;
  color: ${palettes.brand.blue.dark};
  overflow: visible;
  overflow-wrap: break-word;
`;

const TableHeader = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${palettes.brand.blue.darkest};
  overflow: visible;
  overflow-wrap: break-word;
`;

const Cell = ({ label }: { label: string }) => {
  return <TableCell>{label}</TableCell>;
};

const columnDefinition: ColumnDefinition<Detail, ColumnId>[] = [
  {
    id: 'legalNature',
    header: () => <TableHeader>{i18next.t('serasaOccurrenceDetails_legalNature')}</TableHeader>,
    cell: ({ row }) => <Cell label={row.legalNature ?? '-'} />,
    constraints: {
      min: { rem: 8 },
      max: { fr: 1 }
    }
  },
  {
    id: 'creditorName',
    header: () => <TableHeader>{i18next.t('serasaOccurrenceDetails_creditorName')}</TableHeader>,
    cell: ({ row }) => <Cell label={row.creditorName ?? '-'} />,
    constraints: {
      min: { rem: 8 },
      max: { fr: 2 }
    }
  },
  {
    id: 'federalUnit',
    header: () => <TableHeader>{i18next.t('serasaOccurrenceDetails_federalUnit')}</TableHeader>,
    cell: ({ row }) => <Cell label={row.federalUnit ?? '-'} />,
    constraints: {
      min: { rem: 3 },
      max: { fr: 1 }
    }
  },
  {
    id: 'amount',
    header: () => <TableHeader>{i18next.t('serasaOccurrenceDetails_amount')}</TableHeader>,
    cell: ({ row }) => <Cell label={row.amount ? currency.formatBrazilCurrency(row.amount) : '-'} />,
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  },
  {
    id: 'occurrenceDate',
    header: () => <TableHeader>{i18next.t('serasaOccurrenceDetails_date')}</TableHeader>,
    cell: ({ row }) => <Cell label={row.occurrenceDate ? date.formatDate(row.occurrenceDate) : '-'} />,
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  }
];

const rowDefinition: RowDefinition<Detail, ColumnId> = {
  rowId: ({ row }) => row.contractId ?? ''
};

export const SerasaOccurrenceDetailsTable = ({ details }: Props) => {
  return (
    <Card>
      {details.length > 0 ? (
        <>
          <TinyTitle style={{ padding: '1rem 1rem 0.5rem 1rem' }}>
            {i18next.t('serasaOccurrenceDetails_title')}
          </TinyTitle>
          <Table
            label="SerasaOccurrenceDetails"
            tableStyle="inline"
            columnDefinition={columnDefinition}
            rowDefinition={rowDefinition}
            data={details}
          />
        </>
      ) : (
        <SmallBody style={{ padding: '1rem' }}>{i18next.t('serasaOccurrenceDetails_noDetails')}</SmallBody>
      )}
    </Card>
  );
};
